import AuthenticationService from "../../services/AuthenticationService";
import {useIntl} from "react-intl";
import {InternalUser, VisibilityRoles} from "./Analytics.type";
import SearchSelect from "../../ui-components/SearchSelect";
import {useState, useEffect} from "react";
import {PRIVILEGED_ROLES} from "../../constants";
import {arraysOverlap} from "./Analytics.lib";

type Props = {
  internalUsers: InternalUser[];
  visibilityUserPerRole: {[key in VisibilityRoles]?: {label: string; value: number} | undefined};
  setVisibilityUserPerRole: (visibilityUserPerRole: {[key in VisibilityRoles]?: {label: string; value: number} | undefined}) => void;
}

export const VisibilityFilter = ({
    internalUsers,
    visibilityUserPerRole,
    setVisibilityUserPerRole,
  }:Props) => {
  const intl = useIntl();
  const userData = AuthenticationService.getUserData();
  const [visibilityFieldManagerOptions, setVisibilityFieldManagerOptions] = useState<{label: string; value: number}[]>([]);
  const [visibilityFieldManager, setVisibilityFieldManager] =  useState<{label: string; value: number} | undefined>(visibilityUserPerRole['field_manager']);

  const [visibilityFieldOptions, setVisibilityFieldOptions] = useState<{label: string; value: number}[]>([]);
  const [visibilityField, setVisibilityField] =  useState<{label: string; value: number} | undefined>(visibilityUserPerRole['field']);

  const [visibilityAreaManagerOptions, setVisibilityAreaManagerOptions] = useState<{label: string; value: number}[]>([]);
  const [visibilityAreaManager, setVisibilityAreaManager] =  useState<{label: string; value: number} | undefined>(visibilityUserPerRole['area_manager']);
  const [visibilityAgentOptions, setVisibilityAgentOptions] = useState<{label: string; value: number}[]>([]);
  const [visibilityAgent, setVisibilityAgent] =  useState<{label: string; value: number} | undefined>(visibilityUserPerRole['agent']);

  useEffect(() => {
    // when visibilityFieldManager changes, update visibilityFieldOptions to only include fields under that manager
    // use the 'ambito' property of internalUsers to filter
    if (visibilityFieldManager) {
      const chosenManager = internalUsers.find(user => user.id === visibilityFieldManager.value);
      if (!chosenManager) return;
      const filteredFields = internalUsers.filter(user => {
        return user.role.name === 'field' && arraysOverlap(user.ambito, chosenManager.ambito);
      }).map(user => ({
        label: `${user.firstname} ${user.lastname}`,
        value: user.id,
      }));
      setVisibilityFieldOptions(filteredFields);
      // if the current visibilityField is not in the new options, clear it
      if (visibilityField && !filteredFields.find(option => option.value === visibilityField.value)) {
        setVisibilityField(undefined);
        const newVisibilityUserPerRole = {...visibilityUserPerRole, 'field': undefined};
        setVisibilityUserPerRole(newVisibilityUserPerRole);
      }
    } else {
      // if no manager is selected, show all fields
      const allFields = internalUsers.filter(user => {
        return user.role.name === 'field';
      }).map(user => ({
        label: `${user.firstname} ${user.lastname}`,
        value: user.id,
      }));
      setVisibilityFieldOptions(allFields);
    }
  }, [visibilityFieldManager, internalUsers]);

  useEffect(() => {
    // when component mounts, set options for field manager and field
    setVisibilityFieldManagerOptions(internalUsers.filter(user => {
      return user.role.name === 'field_manager';
    }).map(user => ({
      label: `${user.firstname} ${user.lastname}`,
      value: user.id,
    })));
    setVisibilityFieldOptions(internalUsers.filter(user => {
      return user.role.name === 'field';
    }).map(user => ({
      label: `${user.firstname} ${user.lastname}`,
      value: user.id,
    })));
    setVisibilityAreaManagerOptions(internalUsers.filter(user => {
      return user.role.name === 'area_manager';
    }).map(user => ({
      label: `${user.firstname} ${user.lastname}`,
      value: user.id,
    })));

    setVisibilityAgentOptions(internalUsers.filter(user => {
      return (user.role.name === 'agent'
          // if the current user is an area manager, only show agents under their hierarchy, otherwise show all agents
        && (userData.role_type === 'area_manager' ? user.role_hierarchy.split(';').some(rh => rh === `user_${userData.id}`) : true)
      );
    }).map(user => ({
      label: `${user.firstname} ${user.lastname}`,
      value: user.id,
    })));
  }, [internalUsers]);

  // whenever visibilityAreaManager changes, clear visibilityAgent based on role hierarchy
  useEffect(() => {
    // when visibilityAreaManager changes, update visibilityAgentOptions to only include agents under that manager
    // but as only privileged roles can select area manager, this behavior is limited to them
    if (PRIVILEGED_ROLES.concat(['field', 'sales_director']).includes(userData.role_type)) {
      if (visibilityAreaManager) {
        const chosenManager = internalUsers.find(user => user.id === visibilityAreaManager.value);
        if (!chosenManager) return;
        const filteredAgents = internalUsers.filter(user => {
          return user.role.name === 'agent' && user.role_hierarchy.split(';').some(rh => rh === `user_${chosenManager.id}`)
        }).map(user => ({
          label: `${user.firstname} ${user.lastname}`,
          value: user.id,
        }));
        setVisibilityAgentOptions(filteredAgents);
        // if the current visibilityAgent is not in the new options, clear it
        if (visibilityAgent && !filteredAgents.find(option => option.value === visibilityAgent.value)) {
          setVisibilityAgent(undefined);
          const newVisibilityUserPerRole = {...visibilityUserPerRole, 'agent': undefined};
          setVisibilityUserPerRole(newVisibilityUserPerRole);
        }
      } else {
        // if no area manager is selected, show all agents
        const allAgents = internalUsers.filter(user => {
          return user.role.name === 'agent';
        }).map(user => ({
          label: `${user.firstname} ${user.lastname}`,
          value: user.id,
        }));
        setVisibilityAgentOptions(allAgents);
      }
    }
  }, [visibilityAreaManager, internalUsers]);


  return (
    <>
      {PRIVILEGED_ROLES.concat(['field', 'sales_director']).includes(userData.role_type) ? (
        <div className={"flex flex-row gap-12 items-center"}>
          <div>
            {/*FIELD MANAGER*/}
            <SearchSelect
              value={visibilityFieldManager}
              onChange={(value: { label: string; value: number; }) => {
                setVisibilityFieldManager(value);
              }}
              options={visibilityFieldManagerOptions}
              label={undefined}
              placeholder={intl.formatMessage({id: "select_field_manager", defaultMessage: "Seleziona Field Manager"})}
              isClearable
              loadOptions={undefined}
              sMulti={undefined}
              CustomOption={undefined}
              CustomGroup={undefined}
              isMulti={false}
            />
            {/*FIELD*/}
            <SearchSelect
              value={visibilityField}
              onChange={(value: { label: string; value: number; }) => {
                setVisibilityField(value);
                const newVisibilityUserPerRole = {...visibilityUserPerRole, 'field': value};
                setVisibilityUserPerRole(newVisibilityUserPerRole);
              }}
              options={visibilityFieldOptions}
              label={undefined}
              placeholder={intl.formatMessage({id: "select_field", defaultMessage: "Seleziona Field"})}
              isClearable loadOptions={undefined}
              isMulti={false}
              CustomOption={undefined}
              CustomGroup={undefined}
            />
          </div>
          <div>
            {/*AREA MANAGER*/}
            <SearchSelect
              value={visibilityAreaManager}
              onChange={(value: { label: string; value: number; }) => {
                setVisibilityAreaManager(value);
                const newVisibilityUserPerRole = {...visibilityUserPerRole, 'area_manager': value};
                setVisibilityUserPerRole(newVisibilityUserPerRole);
              }}
              options={visibilityAreaManagerOptions}
              label={undefined}
              placeholder={intl.formatMessage({id: "select_area_manager", defaultMessage: "Seleziona Area Manager"})}
              isClearable loadOptions={undefined}
              isMulti={false}
              CustomOption={undefined}
              CustomGroup={undefined}
            />
            {/*AGENT*/}
            <SearchSelect
              value={visibilityAgent}
              onChange={(value: { label: string; value: number; }) => {
                setVisibilityAgent(value);
                const newVisibilityUserPerRole = {...visibilityUserPerRole, 'agent': value};
                setVisibilityUserPerRole(newVisibilityUserPerRole);
              }}
              options={visibilityAgentOptions}
              label={undefined}
              placeholder={intl.formatMessage({id: "select_agent", defaultMessage: "Seleziona Agente"})}
              isClearable loadOptions={undefined}
              isMulti={false}
              CustomOption={undefined}
              CustomGroup={undefined}
            />
          </div>
        </div>
      ): (
        <div className={"flex"}>
          {userData.role_type === 'area_manager' && (
            <SearchSelect
              value={visibilityAgent}
              onChange={(value: { label: string; value: number; }) => {
                setVisibilityAgent(value);
                const newVisibilityUserPerRole = {...visibilityUserPerRole, 'agent': value};
                setVisibilityUserPerRole(newVisibilityUserPerRole);
              }}
              options={visibilityAgentOptions}
              label={undefined}
              placeholder={intl.formatMessage({id: "select_agent", defaultMessage: "Seleziona Agente"})}
              isClearable loadOptions={undefined}
              isMulti={false}
              CustomOption={undefined}
              CustomGroup={undefined}
            />
          )}
        </div>
      )
      }
    </>
  )
}